import { type Header, type Table as TableType, flexRender } from "@tanstack/react-table";
import { ChevronDown, ChevronUp, ChevronsUpDown, Filter } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { SimpleTooltip } from "@/components/shared/simple-tooltip";

import { cn } from "@/lib/utils";

import { Filters } from "./filters";
import { STICKY_END_EDGE } from "./sticky";

interface TableHeaderProps<TData> {
  headerClassName?: string;
  table: TableType<TData>;
}

/** Tugmalarning skrinrider nomi uchun ustun nomi — sarlavha matn bo'lmasa `id`. */
const columnName = <TData,>(header: Header<TData, unknown>): string => {
  const definition = header.column.columnDef.header;
  return typeof definition === "string" ? definition : header.column.id;
};

/** `false` ham qiymat: "ha/yo'q" filtrida "yo'q" tanlangan bo'lishi mumkin. */
const isFiltered = <TData,>(header: Header<TData, unknown>): boolean => {
  const value = header.column.getFilterValue();
  return !!value || value === false;
};

/**
 * Jadval sarlavhasi: nom + filtr + saralash.
 *
 * Filtr jadval tepasidagi panelda emas, aynan o'z ustunida turadi: "bu maydon
 * qaysi ustunga tegishli?" degan savol tug'ilmaydi, faol filtr esa belgining
 * rangidan ko'rinib turadi.
 */
export function TableHeaderComponent<TData>({
  headerClassName,
  table,
}: Readonly<TableHeaderProps<TData>>) {
  const { t } = useTranslation();

  // Tugma nomida ustun ham bor: bir xil "Saralash" degan o'nta tugma
  // skrinriderda bir-biridan farq qilmasdi.
  const sortLabel = (direction: false | "asc" | "desc", column: string): string => {
    if (direction === "asc") return t("TABLE.SORTED_ASC", { column });
    if (direction === "desc") return t("TABLE.SORTED_DESC", { column });
    return t("TABLE.SORT_BY", { column });
  };

  const sortHint = (direction: false | "asc" | "desc"): string => {
    if (direction === "asc") return t("TABLE.ASCENDING");
    if (direction === "desc") return t("TABLE.DESCENDING");
    return t("TABLE.SORT");
  };

  const renderFilterButton = (header: Header<TData, unknown>) => {
    const active = isFiltered(header);

    return (
      <Popover>
        <SimpleTooltip description={t("TABLE.FILTER")}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={t("TABLE.FILTER_BY", { column: columnName(header) })}
              aria-pressed={active}
              className="focus-visible:ring-ring/50 ml-1 cursor-pointer rounded-sm outline-none focus-visible:ring-2"
            >
              <Filter
                className={cn("size-4", active ? "text-destructive" : "text-muted-foreground")}
              />
            </button>
          </PopoverTrigger>
        </SimpleTooltip>
        <PopoverContent align="end" className="w-auto gap-0 p-0">
          <Filters column={header.column} label={columnName(header)} />
        </PopoverContent>
      </Popover>
    );
  };

  const renderSortButton = (header: Header<TData, unknown>) => {
    const direction = header.column.getIsSorted();

    return (
      <SimpleTooltip description={sortHint(direction)}>
        <button
          type="button"
          className="focus-visible:ring-ring/50 cursor-pointer rounded-sm outline-none focus-visible:ring-2"
          aria-label={sortLabel(direction, columnName(header))}
          onClick={header.column.getToggleSortingHandler()}
        >
          {direction === "asc" && <ChevronUp className="size-4" />}
          {direction === "desc" && <ChevronDown className="size-4" />}
          {!direction && <ChevronsUpDown className="text-muted-foreground size-4" />}
        </button>
      </SimpleTooltip>
    );
  };

  return (
    <TableHeader className={cn("bg-secondary sticky -top-px z-10", headerClassName)}>
      {table.getHeaderGroups().map(headerGroup => (
        <TableRow key={headerGroup.id} className="hover:bg-transparent">
          {headerGroup.headers.map(header => {
            const columnMeta = header.column.columnDef.meta;
            const columnId = header.column.id;
            const isActionColumn = ["actions", "id", "index"].includes(columnId);
            const direction = header.column.getIsSorted();

            return (
              <TableHead
                key={header.id}
                aria-sort={
                  direction === "asc"
                    ? "ascending"
                    : direction === "desc"
                      ? "descending"
                      : undefined
                }
                className={cn("relative h-7 border px-2 text-xs", {
                  "w-14 justify-center gap-0": isActionColumn,
                  [`bg-secondary sticky right-0 z-10 ${STICKY_END_EDGE}`]: columnId === "actions",
                  "min-w-14": !columnMeta?.width,
                })}
                style={
                  columnMeta?.width
                    ? {
                        width: columnMeta.width,
                        minWidth: columnMeta.width,
                        maxWidth: columnMeta.width,
                      }
                    : {}
                }
              >
                <div
                  className={cn("flex items-center justify-between gap-2", {
                    "justify-center gap-0": isActionColumn,
                  })}
                >
                  {/* `min-w-0 truncate` — uzun sarlavha filtr va saralash
                      tugmalarini katakdan tashqariga surib chiqarmasin. */}
                  <span className="min-w-0 truncate">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </span>

                  <div className="flex shrink-0 gap-1">
                    {header.column.getCanFilter() &&
                      columnMeta?.filterVariant &&
                      renderFilterButton(header)}
                    {header.column.getCanSort() && renderSortButton(header)}
                  </div>
                </div>
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
}
